import { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import AskCargoDash from "../components/AskCargoDash";
import { useAuth } from "../context/AuthContext";
import { Menu } from "lucide-react";

/**
 * Detect Hub-embedded mode: the only signal is the iframe parent check.
 * No `?hub=1` flag, no custom header. When embedded, the Hub shell owns
 * the top chrome, so we hide our sidebar and mobile-header.
 */
function useEmbeddedMode() {
  const [embedded, setEmbedded] = useState(false);
  useEffect(() => {
    try {
      setEmbedded(window.parent !== window);
    } catch {
      setEmbedded(true);  // cross-origin access denied implies framed
    }
  }, []);
  return embedded;
}

export default function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const embedded = useEmbeddedMode();
  const aiAllowed = user?.role && ["CM", "SM", "SA", "CI"].includes(user.role);

  return (
    <div className="flex min-h-screen bg-[#F5F5F5]" data-embedded={embedded ? "true" : "false"}>
      {/* Sidebar is ALWAYS rendered — even in Hub-embedded mode users need
          in-app navigation between Jobs / Capture / Dashboard / etc. The
          only thing the Hub shell owns is the top chrome (its own header
          + user menu), not our in-app sidebar. */}
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <div className="flex-1 lg:ml-60">
        {/* Mobile header — hidden when embedded since the Hub owns the top chrome */}
        {!embedded && (
          <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileOpen(true)}
              className="text-slate-600 hover:text-slate-900"
            >
              <Menu size={22} />
            </button>
            <img src="/cargodash-logo.svg" alt="CargoDash" className="h-6" />
          </div>
        )}
        <main className="p-4 md:p-6 overflow-auto" data-testid="main-content">
          <Outlet />
        </main>
      </div>
      {aiAllowed && <AskCargoDash />}
    </div>
  );
}
